<template>
    <!-- eslint-disable vue/no-unused-refs -->
    <SbsCheckbox
        v-bind="itemData"
        v-model="selectedModel"
        ref="checkbox"
        class="sbs-select-list-item">
        <!-- eslint-enable vue/no-unused-refs -->
        <template #prepend>
            <!-- @slot Слот для вставки контента до элемента -->
            <slot name="prepend"></slot>
        </template>

        <template #control="{ modelValue: selected, valid }">
            <!-- @slot Слот для кастомного элемента управления -->
            <slot
                v-if="!hideControl"
                name="control"
                v-bind="{
                    selected,
                    valid,
                }"></slot>
        </template>

        <template #default>
            <!-- @slot Слот по-умолчанию для вставки контента -->
            <slot v-bind="{ selected: selectedModel }"></slot>
        </template>
    </SbsCheckbox>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';

/**
 * Компонент элемента списка
 */
export default {
    name: 'SbsSelectListItem',
    mixins: [themesMixin],
    inject: {
        listItemGroupComponent: { default: null },
    },
    emits: ['update:modelValue'],
    computed: {
        isGroup() {
            return Boolean(this.listItemGroupComponent);
        },
        listItemGroupMandatory() {
            return this.listItemGroupComponent?.mandatory;
        },
        listItemGroupName() {
            return this.listItemGroupComponent?.name;
        },
        listItemGroupHideControl() {
            return this.listItemGroupComponent?.hideControl;
        },
        listItemGroupDisabled() {
            return this.listItemGroupComponent?.disabled;
        },
        listItemModel() {
            return this.isGroup
                ? this.listItemGroupComponent?.modelValue
                : this.modelValue;
        },
        listItemMultiple() {
            return Array.isArray(this.listItemModel);
        },
        selectedModel: {
            get() {
                let value = this.listItemModel;

                if (typeof value === 'string') {
                    // Элемент выбран, если текущая модель элемента равна значению элемента
                    value = value === this.modelValue;
                } else if (this.listItemMultiple) {
                    // Элемент выбран, если текущая модель элемента содержит его значение
                    value = value.includes(this.modelValue);
                }

                return value;
            },
            set(value) {
                let emittedValue = value;

                if (typeof this.listItemModel === 'string') {
                    // Если элемент выбран, возвращаем его значение, иначе - пустую строку
                    emittedValue = value ? this.modelValue : '';
                } else if (this.listItemMultiple) {
                    // Если элемент выбран, добавляем его значение в массив выбранных значений, иначе - удаляем его оттуда
                    emittedValue = value
                        ? [...this.listItemModel, this.modelValue]
                        : this.listItemModel
                            .filter(item => item !== this.modelValue);
                }

                // Если элемент отмечает сразу несколько значений
                if (this.selectValues.length) {
                    emittedValue = value
                        ? [
                            this.modelValue, // добавляем текущий элемент
                            ...this.listItemModel, // добавляем уже выбранные элементы
                            ...this.unselectedItems, // добавляем оставшиеся невыбранные элементы из `selectValues`
                        ]
                        : this.listItemModel.filter(item => (
                            item !== this.modelValue && // удаляем текущий элемент
                            !this.selectValues.includes(item) // удаляем элементы из `selectValues`
                        ));
                }

                if (this.listItemGroupMandatory) {
                    if (!emittedValue || !emittedValue?.length) return;
                }

                this.emitValue(emittedValue);
            },
        },
        unselectedItems() {
            return this.selectValues.filter(val => !this.listItemModel
                .includes(val));
        },
        isDisabled() {
            return this.disabled || this.listItemGroupDisabled;
        },
        listItemName() {
            return this.name || this.listItemGroupName;
        },
        listItemHideControl() {
            return this.hideControl || this.listItemGroupHideControl;
        },
        itemData() {
            return {
                ...this.$attrs,
                ...this.themeData,
                controlTheme: this.controlTheme,
                disabled: this.isDisabled,
                fullWidth: this.fullWidth,
                hideControl: this.listItemHideControl,
                hoverBackground: this.hoverBackground,
                hoverControl: this.hoverControl,
                id: this.id,
                label: this.label,
                labelClass: this.labelClass,
                name: this.listItemName,
                prepend: this.prepend,
                selectedClass: this.selectedClass,
            };
        },
    },
    methods: {
        emitValue(value) {
            if (this.isGroup) {
                /**
                 * Событие `SbsSelectList:change` срабатывает при изменении значения,
                 * если SbsSelectListItem используется в связке с комопнентом SbsSelectList.
                 * В качестве параметра - значение после изменения
                 */
                if (this.listItemGroupComponent) {
                    this.listItemGroupComponent.listItemChangeHandler(value);
                }
            } else {
                /**
                 * Событие `update:modelValue` срабатывает при изменении значения.
                 * В качестве параметра - значение после изменения
                 */
                this.$emit('update:modelValue', value);
            }
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Состояние элемента (выбран или нет).
         * При использовании группы элементов принимает массив выбранных значений
         * @model
         */
        modelValue: {
            type: [String, Boolean, Array],
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета фона элемента управления
         */
        controlTheme: {
            type: String,
            default: '',
        },
        /**
         * Перевод в неактивное состояние
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет будет ли элемент занимать всю доступную ширину
         */
        fullWidth: {
            type: Boolean,
            default: false,
        },
        /**
         * Скрывает элемент управления
         */
        hideControl: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета фона при наведении
         */
        hoverBackground: {
            type: String,
            default: AllColors.GRAY_ULTRA_LIGHT,
        },
        /**
         * Тема (цветовые решения) для цвета фона элемента управления при наведении
         */
        hoverControl: {
            type: String,
            default: '',
        },
        /**
         * Уникальный идентификатор
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Контент, расположенный после чекбокса
         */
        label: {
            type: String,
            default: '',
        },
        /**
         * Класс для лэйбла
         */
        labelClass: {
            type: String,
            default: '',
        },
        /**
         * Название чекбокса, используется для объединения в общую группу
         */
        name: {
            type: String,
            default: '',
        },
        /**
         * Контент, расположенный перед чекбоксом
         */
        prepend: {
            type: String,
            default: '',
        },
        /**
         * Класс для активного состояния (чекбокс выбран)
         */
        selectedClass: {
            type: String,
            default: '',
        },
        /**
         * Позволяет задать значения других элементов, которые будут выбраны
         * вместе с текущим элементом, например для реализации "Выбрать все".
         *
         * Применяется только в случае, если для `SbsSelectList` тип v-model
         * является массивом
         */
        selectValues: {
            type: Array,
            default: () => [],
        },
        /**
         * Значение элемента
         */
        value: {
            type: String,
            default: '',
        },
    },
    watch: {
        listItemModel(newVal) {
            if (!this.listItemMultiple) return;

            if (this.selectValues.length) {
                let emittedValue = [];
                const allSelected = this.selectValues
                    .every(value => newVal.includes(value));

                if (this.selectedModel && !allSelected) {
                    emittedValue = newVal
                        .filter(value => value !== this.modelValue);
                    this.emitValue(emittedValue);
                }

                if (!this.selectedModel && allSelected) {
                    emittedValue = [
                        this.modelValue,
                        ...this.listItemModel,
                    ];
                    this.emitValue(emittedValue);
                }
            }
        },
    },
    mounted() {
        if (this.selectValues.length && this.selectedModel) {
            const emittedValue = [...this.listItemModel];

            if (this.selectedModel) {
                emittedValue.push(...this.unselectedItems);
            } else if (!this.unselectedItems.length) {
                emittedValue.push(this.modelValue);
            }

            this.emitValue(emittedValue);

            if (!this.listItemMultiple) {
                console.warn(`\`SbsSelectListItem\` (value=\`${this.modelValue}\`) prop \`selectedValues\` does not applies if \`SbsSelectList\` model type is not an array`);
            }
        }
    },
};
</script>

<style lang="scss">
.sbs-select-list-item {
    display: flex;
}
</style>
